import React, { useState, useRef, useCallback, useEffect } from 'react';
import { getData, postData } from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';

const excelDateToString = (value) => {
  if (!value) return '';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num) || num < 10000 || num > 100000) return value?.toString() || '';
  const excelEpoch = new Date(1899, 11, 30);
  const adjusted = num > 59 ? num - 1 : num;
  const date = new Date(excelEpoch.getTime() + (adjusted - 1) * 24 * 60 * 60 * 1000);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatValue = (value, col) => {
  if (value === null || value === undefined || value === '') return '-';
  if (col.key.includes('Date')) return excelDateToString(value);
  return String(value);
};

export default function DataMigration() {
  useAuth();
  const fileInputRef = useRef(null);
  const [activeTab, setActiveTab] = useState('batch');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [parseResult, setParseResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importedBatches, setImportedBatches] = useState([]);
  const [importedPagination, setImportedPagination] = useState({ page: 1, limit: 10, total: 0, pages: 1 });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [errorsDialog, setErrorsDialog] = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const columns = activeTab === 'batch' ? [
    { key: 'batchNumber', label: 'Batch Number', required: true },
    { key: 'courseId', label: 'Course ID', required: true },
    { key: 'courseName', label: 'Course Name', required: false },
    { key: 'facultyId', label: 'Faculty ID', required: false },
    { key: 'facultyName', label: 'Faculty Name', required: false },
    { key: 'timingStartTime', label: 'Start Time', required: false },
    { key: 'timingEndTime', label: 'End Time', required: false },
    { key: 'classroom', label: 'Classroom', required: false },
    { key: 'startDate', label: 'Start Date', required: false },
    { key: 'endDate', label: 'End Date', required: false },
    { key: 'status', label: 'Status', required: false },
  ] : [
    { key: 'studentName', label: 'Student Name', required: true },
    { key: 'course', label: 'Course', required: false },
    { key: 'batch', label: 'Batch', required: false },
    { key: 'totalFee', label: 'Total Fee', required: false },
    { key: 'paidAmount', label: 'Paid Amount', required: false },
    { key: 'balanceAmount', label: 'Balance Amount', required: false },
    { key: 'paymentDate', label: 'Payment Date', required: false },
    { key: 'paymentMode', label: 'Payment Mode', required: false },
    { key: 'remarks', label: 'Remarks', required: false },
  ];

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await getData('/training/data-migration/history');
      if (r.success) {
        setHistory(r.records || []);
      }
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = e.dataTransfer?.files;
    if (files?.[0]) validateAndUpload(files[0]);
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files?.[0]) validateAndUpload(files[0]);
  };

  const validateAndUpload = async (file) => {
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!['.xlsx', '.xls', '.csv'].includes(ext)) {
      alert('Please upload a valid Excel or CSV file');
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      alert('File size exceeds 50MB limit');
      return;
    }
    setUploadedFile(file);
    setImportResult(null);
    setImportedBatches([]);
    setImportErrors([]);
    setPreviewData([]);
    setColumnMapping({});

    const formData = new FormData();
    formData.append('file', file);
    formData.append('module', activeTab);

    try {
      const res = await postData('/training/data-migration/upload', formData, true);
      if (res.success) {
        const data = res.data || res;
        setParseResult({
          sheetName: data.sheetName || 'Sheet1',
          headers: data.headers || [],
          totalRows: data.totalRows || 0,
          columnMapping: data.columnMapping || {},
          previewRows: data.previewRows || [],
          rows: data.rows || [],
        });
      } else {
        alert(res.message || 'Failed to parse file');
        setUploadedFile(null);
      }
    } catch (err) {
      alert('Parse failed: ' + (err.message || 'Unknown error'));
      setUploadedFile(null);
    }
  };

  const handlePreview = () => {
    if (!parseResult || !parseResult.rows.length) {
      alert('No data to preview');
      return;
    }

    const mappedData = parseResult.rows.slice(0, 10).map((row, idx) => {
      const mapped = { _errors: [] };
      Object.entries(columnMapping).forEach(([field, header]) => {
        const colIndex = parseResult.headers.indexOf(header);
        if (colIndex >= 0 && colIndex < row.length) {
          mapped[field] = row[colIndex];
        }
      });
      return mapped;
    });

    setPreviewData(mappedData);
  };

  const getColumnMappingIndices = () => {
    const indices = {};
    Object.entries(columnMapping).forEach(([field, header]) => {
      const colIndex = parseResult.headers.indexOf(header);
      if (colIndex >= 0) {
        indices[field] = colIndex;
      }
    });
    return indices;
  };

  const handleImport = async () => {
    if (!previewData.length) return;
    if (previewData.some(p => p._errors?.length > 0)) {
      alert('Please fix all errors before importing');
      return;
    }

    setImporting(true);
    setImportProgress(0);
    setImportErrors([]);

    try {
      const res = await postData('/training/data-migration/execute', {
        module: activeTab,
        columnMapping: getColumnMappingIndices(),
        data: parseResult.rows,
        fileName: uploadedFile.name,
      });

      if (res.success) {
        const data = res.data || res;
        setImportResult({
          successRows: data.successRows || 0,
          failedRows: data.failedRows || 0,
        });
        setImportErrors(data.errors || []);
        setImportedBatches(data.importedBatches || []);
        setImportedPagination(prev => ({
          ...prev,
          total: data.importedBatches?.length || 0,
          pages: Math.ceil((data.importedBatches?.length || 0) / prev.limit) || 1,
        }));
        if (activeTab === 'history') fetchHistory();
      } else {
        alert(res.message || 'Import failed');
      }
    } catch (err) {
      alert('Import failed: ' + (err.message || 'Unknown error'));
    }
    setImporting(false);
  };

  const handleRollback = async (id) => {
    if (!window.confirm('Are you sure you want to rollback this import?')) return;
    setRollbackLoading(true);
    try {
      const res = await postData(`/training/data-migration/rollback/${id}`, {});
      alert(res.message || 'Rollback successful');
      fetchHistory();
    } catch (err) {
      alert('Rollback failed: ' + (err.message || err.response?.data?.message || 'Unknown error'));
    }
    setRollbackLoading(false);
  };

  const handleDownloadErrors = async (id) => {
    try {
      const res = await getData(`/training/data-migration/errors/${id}`, {}, true);
      const url = window.URL.createObjectURL(new Blob([res]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `import_errors_${id}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Download failed');
    }
  };

  const resetImport = () => {
    setUploadedFile(null);
    setParseResult(null);
    setColumnMapping({});
    setPreviewData([]);
    setImportResult(null);
    setImportedBatches([]);
    setImportErrors([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadSample = () => {
    const sampleHeaders = activeTab === 'batch'
      ? ['Batch Number', 'Course ID', 'Course Name', 'Faculty ID', 'Faculty Name', 'Start Time', 'End Time', 'Classroom', 'Start Date', 'End Date', 'Status']
      : ['Student Name', 'Course', 'Batch', 'Total Fee', 'Paid Amount', 'Balance Amount', 'Payment Date', 'Payment Mode', 'Remarks'];
    const sampleRow = activeTab === 'batch'
      ? ['BATCH001', 'C001', 'React Course', 'F001', 'John Doe', '09:00', '11:00', 'Room A', '01/01/2024', '01/03/2024', 'ongoing']
      : ['John Smith', 'React Course', 'BATCH001', '50000', '25000', '25000', '01/01/2024', 'Cash', 'First installment'];
    
    const csvContent = [sampleHeaders.join(','), sampleRow.join(',')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sample_${activeTab}_import.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Data Migration</h4>
          <p className="text-muted small mb-0">Import historical data from Excel files</p>
        </div>
        <span className="badge bg-danger border border-danger">Confidential</span>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'batch' ? 'active' : ''}`} onClick={() => setActiveTab('batch')}>
            Batch Data Import
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'fee' ? 'active' : ''}`} onClick={() => setActiveTab('fee')}>
            Fee Data Import
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
            <i className="bi bi-clock-history me-1"></i>Import History
          </button>
        </li>
      </ul>

      {activeTab !== 'history' && (
        <div className="row">
          <div className="col-md-4">
            <div className="card mb-3">
              <div className="card-header">
                <h6 className="mb-0">{activeTab === 'batch' ? 'Batch' : 'Fee'} Data Import</h6>
              </div>
              <div className="card-body">
                {!uploadedFile ? (
                  <div
                    className={`border border-primary border-dashed rounded text-center p-4 ${dragActive ? 'bg-primary bg-opacity-10' : ''}`}
                    onDragOver={handleDrag}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    style={{ cursor: 'pointer' }}
                  >
                    <i className="bi bi-cloud-upload text-primary" style={{ fontSize: '3rem' }}></i>
                    <p className="fw-500 mt-2 mb-1">Drag & Drop or Click to Upload</p>
                    <p className="text-muted small mb-0">Supported: .xlsx, .xls, .csv (Max 50MB)</p>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept=".xlsx,.xls,.csv"
                      style={{ display: 'none' }}
                      onChange={handleFileSelect}
                    />
                  </div>
                ) : (
                  <div>
                    <div className="d-flex align-items-center gap-2 p-2 bg-light rounded">
                      <i className="bi bi-file-earmark-excel text-primary fs-4"></i>
                      <div className="flex-grow-1">
                        <div className="fw-500 small">{uploadedFile.name}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                        </div>
                      </div>
                      <button className="btn btn-sm text-danger" onClick={resetImport}>
                        <i className="bi bi-x-lg"></i>
                      </button>
                    </div>

                    {parseResult && (
                      <div className="mt-2 small text-muted">
                        Sheet: {parseResult.sheetName} | Rows: {parseResult.totalRows} | Columns: {parseResult.headers.length}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {parseResult && (
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">Column Mapping</h6>
                  <small className="text-muted">Map Excel columns to fields</small>
                </div>
                <div className="card-body">
                  {columns.map(col => (
                    <div key={col.key} className="mb-2">
                      <label className="form-label small">
                        {col.label} {col.required && <span className="text-danger">*</span>}
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={columnMapping[col.key] || ''}
                        onChange={(e) => setColumnMapping(prev => ({ ...prev, [col.key]: e.target.value }))}
                      >
                        <option value="">-- Select Column --</option>
                        {parseResult.headers.map((header, idx) => (
                          <option key={idx} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}

                  <button
                    className="btn btn-outline-primary btn-sm w-100 mt-2"
                    onClick={handlePreview}
                  >
                    Preview Data
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm w-100 mt-2"
                    onClick={handleDownloadSample}
                  >
                    <i className="bi bi-download me-1"></i>Download Sample
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="col-md-8">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Data Preview</h6>
                {previewData.length > 0 && !importing && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={handleImport}
                    disabled={previewData.some(p => p._errors?.length > 0)}
                  >
                    <i className="bi bi-download me-1"></i>Start Import
                  </button>
                )}
              </div>
              <div className="card-body">
                {importing && (
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-1">
                      <small>Importing...</small>
                      <small>{importProgress}%</small>
                    </div>
                    <div className="progress" style={{ height: '6px' }}>
                      <div className="progress-bar" style={{ width: `${importProgress}%` }}></div>
                    </div>
                  </div>
                )}

                {!importResult && previewData.length === 0 && !importing && (
                  <div className="text-center py-5 text-muted">
                    <i className="bi bi-table" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                    <p className="mt-2">Upload a file and map columns to preview data</p>
                  </div>
                )}

                {previewData.length > 0 && !importResult && (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: '40px' }}>#</th>
                          {columns.map(col => (
                            <th key={col.key}>{col.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.map((row, idx) => (
                          <tr key={idx} className={row._errors?.length > 0 ? 'table-danger' : ''}>
                            <td>{idx + 1}</td>
                            {columns.map(col => (
                              <td key={col.key}>
                                <div className="d-flex align-items-center gap-1">
                                  {formatValue(row[col.key], col)}
                                  {row._errors?.length > 0 && row._errors.some(e => e.includes(col.label)) && (
                                    <i className="bi bi-exclamation-circle text-danger" title={row._errors.join(', ')}></i>
                                  )}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {importResult && importedBatches.length > 0 && (
        <div className="row mt-3">
          <div className="col-12">
            <div className={`alert ${importResult.failedRows > 0 ? 'alert-warning' : 'alert-success'} d-flex justify-content-between align-items-center`}>
              <span><i className={`bi ${importResult.failedRows > 0 ? 'bi-exclamation-triangle-fill' : 'bi-check-circle-fill'} me-2`}></i>Import completed! {importResult.successRows} batches imported successfully.</span>
              {importResult.failedRows > 0 && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => setErrorsDialog('failed')}
                >
                  View {importResult.failedRows} Failed Rows
                </button>
              )}
            </div>
            <div className="card">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <h6 className="mb-0"><i className="bi bi-list-check me-2"></i>Imported Batches ({importResult.successRows})</h6>
                <button
                  className="btn btn-sm btn-light"
                  onClick={() => {
                    setImportResult(null);
                    setImportedBatches([]);
                    setImportErrors([]);
                  }}
                >
                  <i className="bi bi-x-circle me-1"></i>Clear
                </button>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-sm table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: '50px', textAlign: 'center' }}>#</th>
                        <th style={{ minWidth: '100px' }}>Batch Number</th>
                        <th style={{ minWidth: '150px' }}>Course</th>
                        <th style={{ minWidth: '150px' }}>Faculty</th>
                        <th style={{ minWidth: '150px' }}>Batch Time</th>
                        <th style={{ minWidth: '100px' }}>Classroom</th>
                        <th style={{ minWidth: '120px' }}>Start Date</th>
                        <th style={{ minWidth: '120px' }}>End Date</th>
                        <th style={{ minWidth: '100px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importedBatches
                        .slice((importedPagination.page - 1) * importedPagination.limit, importedPagination.page * importedPagination.limit)
                        .map((batch, idx) => (
                        <tr key={idx}>
                          <td style={{ textAlign: 'center' }}>{(importedPagination.page - 1) * importedPagination.limit + idx + 1}</td>
                          <td><strong>{batch.batchNumber || '-'}</strong></td>
                          <td>{batch.courseId || '-'}</td>
                          <td>{batch.facultyName || '-'}</td>
                          <td>{batch.batchTime || '-'}</td>
                          <td>{batch.classroom || '-'}</td>
                          <td>{batch.startDate ? excelDateToString(batch.startDate) : '-'}</td>
                          <td>{batch.endDate ? excelDateToString(batch.endDate) : '-'}</td>
                          <td><span className={`badge ${batch.status === 'ongoing' ? 'bg-primary' : 'bg-secondary'}`}>{batch.status || 'ongoing'}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center p-2 border-top">
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">Rows per page:</span>
                    <select
                      className="form-select form-select-sm"
                      style={{ width: '70px' }}
                      value={importedPagination.limit}
                      onChange={(e) => {
                        const newLimit = parseInt(e.target.value);
                        setImportedPagination(prev => ({ ...prev, limit: newLimit, page: 1, pages: Math.ceil(prev.total / newLimit) || 1 }));
                      }}
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">
                      Showing {Math.min((importedPagination.page - 1) * importedPagination.limit + 1, importedPagination.total)} to {' '}
                      {Math.min(importedPagination.page * importedPagination.limit, importedPagination.total)} of {' '}
                      {importedPagination.total}
                    </span>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-secondary"
                        disabled={importedPagination.page <= 1}
                        onClick={() => setImportedPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                      <button
                        className="btn btn-outline-secondary"
                        disabled={importedPagination.page >= importedPagination.pages}
                        onClick={() => setImportedPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card">
          <div className="card-header">
            <h6 className="mb-0">Import History</h6>
          </div>
          <div className="card-body">
            {historyLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="bi bi-clock-history" style={{ fontSize: '3rem', opacity: 0.3 }}></i>
                <p className="mt-2">No import history found</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-sm table-hover">
                  <thead>
                    <tr>
                      <th>File Name</th>
                      <th>Module</th>
                      <th>Imported By</th>
                      <th>Date</th>
                      <th className="text-center">Total</th>
                      <th className="text-center">Success</th>
                      <th className="text-center">Failed</th>
                      <th>Status</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h._id}>
                        <td>{h.fileName}</td>
                        <td className="text-capitalize">{h.module}</td>
                        <td>{h.importedBy?.name || '-'}</td>
                        <td>{new Date(h.createdAt).toLocaleDateString('en-GB')}</td>
                        <td className="text-center">{h.totalRows || 0}</td>
                        <td className="text-center text-success">{h.successRows || 0}</td>
                        <td className="text-center text-danger">{h.failedRows || 0}</td>
                        <td><span className={`badge bg-${h.status === 'completed' ? 'success' : h.status === 'failed' ? 'danger' : 'warning'}`}>{h.status}</span></td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            {h.errorLog?.length > 0 && (
                              <button
                                className="btn btn-outline-secondary"
                                title="View Errors"
                                onClick={() => { setSelectedHistory(h); setErrorsDialog(true); }}
                              >
                                <i className="bi bi-eye"></i>
                              </button>
                            )}
                            {h.errorLog?.length > 0 && (
                              <button
                                className="btn btn-outline-secondary"
                                title="Download Errors"
                                onClick={() => handleDownloadErrors(h._id)}
                              >
                                <i className="bi bi-download"></i>
                              </button>
                            )}
                            {h.status === 'completed' && (
                              <button
                                className="btn btn-outline-warning"
                                title="Rollback Import"
                                onClick={() => handleRollback(h._id)}
                                disabled={rollbackLoading}
                              >
                                <i className="bi bi-arrow-counterclockwise"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {errorsDialog && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Failed Rows ({importErrors.length})</h5>
                <button type="button" className="btn-close" onClick={() => { setErrorsDialog(null); setSelectedHistory(null); setImportErrors([]); }}></button>
              </div>
              <div className="modal-body">
                {importErrors.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-danger">
                        <tr>
                          <th>Row #</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importErrors.map((err, idx) => (
                          <tr key={idx}>
                            <td>{err.row}</td>
                            <td className="text-danger">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : selectedHistory?.errorLog?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead className="table-light">
                        <tr>
                          <th>Row</th>
                          <th>Error</th>
                          <th>Data</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedHistory.errorLog.map((err, idx) => (
                          <tr key={idx}>
                            <td>{err.row}</td>
                            <td className="text-danger">{err.error}</td>
                            <td style={{ fontSize: '0.75rem' }}>{JSON.stringify(err.data)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-muted">No errors found.</p>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { setErrorsDialog(null); setSelectedHistory(null); setImportErrors([]); }}>
                  Close
                </button>
                {selectedHistory && errorsDialog === 'failed' && (
                  <button className="btn btn-primary" onClick={() => handleDownloadErrors(selectedHistory._id)}>
                    Download CSV
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
